import Phaser from 'phaser';
import { makeTwoPassText, type TwoPassText } from './twoPassText';

// Standard pill-button style used across the app (Interstitial, OhNo,
// DogTraining Play!, Settings, confirmation dialogs):
//   - rounded ends (radius = height/2)
//   - yellow fill (FILL_ON) by default; FILL_OFF for the gray "No" variant
//   - red stroke (STROKE), 6px
//   - two-pass text label
//   - invisible interactive hit-rect sitting on top so the click handler
//     fires regardless of where on the pill you tap
export const FILL_ON = 0xffeb00;
export const FILL_OFF = 0xdddddd;
const STROKE = 0xcc0000;
const STROKE_WIDTH = 6;
const DEFAULT_HEIGHT = 96;
const DEFAULT_PAD_X = 96;
const DEFAULT_FONT_SIZE = '52px';

export interface StandardButtonOpts {
  scene: Phaser.Scene;
  x: number;
  y: number;
  label: string;
  fontSize?: string;
  fillColor?: number;
  paddingX?: number;
  height?: number;
  onClick: () => void;
  baseDepth?: number;
}

export interface StandardButton {
  bg: Phaser.GameObjects.Graphics;
  hit: Phaser.GameObjects.Rectangle;
  text: TwoPassText;
  width: number;
  height: number;
  // All three game objects, in z-order. Useful for adding to a Container.
  objects: Phaser.GameObjects.GameObject[];
  destroy(): void;
}

// --- Paw button (new style) -------------------------------------------------
// Rounded-rectangle pill with a light salmon fill and a thin red border. Red
// paw icon on the left (tinted from bestsquarec). Two-pass text label in the
// middle. Optional `>>` chevrons on the right.
const PAW_FILL = 0xf9ad81;
const PAW_BORDER = 0xff0000;
const PAW_BORDER_WIDTH = 3;
const PAW_CORNER_RADIUS = 18;
const PAW_DEFAULT_HEIGHT = 64;

export interface PawButtonOpts {
  scene: Phaser.Scene;
  x: number;
  y: number;
  label: string;
  fontSize?: string;
  height?: number;
  showChevrons?: boolean;
  // Override the default salmon fill (e.g. FILL_OFF for a gray "No" variant).
  fillColor?: number;
  baseDepth?: number;
  // When false, no interactive hit-rect is built. Use for visual-only pills
  // where a scene-level pointerdown handles dismissal (otherwise both the
  // hit-rect and the scene listener fire and you double-advance).
  interactive?: boolean;
  onClick?: () => void;
}

export interface PawButton {
  bg: Phaser.GameObjects.Graphics;
  hit: Phaser.GameObjects.Rectangle | null;
  text: TwoPassText;
  paw: Phaser.GameObjects.Image;
  chevrons: TwoPassText | null;
  width: number;
  height: number;
  objects: Phaser.GameObjects.GameObject[];
  destroy(): void;
}

export function makePawButton(opts: PawButtonOpts): PawButton {
  const {
    scene, x, y, label,
    fontSize = '40px',
    height = PAW_DEFAULT_HEIGHT,
    showChevrons = false,
    fillColor = PAW_FILL,
    onClick,
    baseDepth = 10,
  } = opts;

  // Probe label + chevrons widths offscreen to compute button width.
  const text = makeTwoPassText(scene, x, y, label, {
    fontSize,
    depth: baseDepth + 1,
  });
  const textW = text.fill.width - 32;

  let chevrons: TwoPassText | null = null;
  let chevW = 0;
  if (showChevrons) {
    chevrons = makeTwoPassText(scene, x, y, '»', {
      fontSize,
      depth: baseDepth + 1,
    });
    chevW = chevrons.fill.width - 32;
  }

  const pawSize = Math.round(height * 0.75);
  const pad = 16;
  const gap = 14;
  const width = Math.round(
    pad + pawSize + gap + textW + (showChevrons ? gap + chevW : 0) + pad,
  );

  // Background pill.
  const bg = scene.add.graphics().setDepth(baseDepth);
  bg.fillStyle(fillColor, 1);
  bg.fillRoundedRect(x - width / 2, y - height / 2, width, height, PAW_CORNER_RADIUS);
  bg.lineStyle(PAW_BORDER_WIDTH, PAW_BORDER, 1);
  bg.strokeRoundedRect(x - width / 2, y - height / 2, width, height, PAW_CORNER_RADIUS);

  // Paw icon — tint the purple bestsquarec to red.
  const pawX = x - width / 2 + pad + pawSize / 2;
  const paw = scene.add
    .image(pawX, y, 'bestsquarec')
    .setDisplaySize(pawSize, pawSize)
    .setOrigin(0.5)
    .setDepth(baseDepth + 1)
    .setTint(PAW_BORDER);

  // Position the label centered between (paw right edge) and (right pad OR
  // chevron left edge). Move the two-pass text to that center.
  const contentLeft = pawX + pawSize / 2 + gap;
  const contentRight = x + width / 2 - pad - (showChevrons ? chevW + gap : 0);
  const labelCx = (contentLeft + contentRight) / 2;
  text.outline.setX(labelCx);
  text.fill.setX(labelCx);

  if (chevrons) {
    const chevCx = x + width / 2 - pad - chevW / 2;
    chevrons.outline.setX(chevCx);
    chevrons.fill.setX(chevCx);
  }

  // Invisible interactive hit-rect on top so taps anywhere on the pill fire.
  let hit: Phaser.GameObjects.Rectangle | null = null;
  if (opts.interactive !== false) {
    hit = scene.add
      .rectangle(x, y, width, height, 0x000000, 0)
      .setOrigin(0.5)
      .setDepth(baseDepth + 2)
      .setInteractive({ useHandCursor: true });
    if (onClick) hit.on('pointerdown', onClick);
  }

  const objects: Phaser.GameObjects.GameObject[] = [
    bg, paw, text.outline, text.fill,
  ];
  if (chevrons) objects.push(chevrons.outline, chevrons.fill);
  if (hit) objects.push(hit);

  return {
    bg, hit, text, paw, chevrons, width, height, objects,
    destroy(): void {
      text.destroy();
      chevrons?.destroy();
      paw.destroy();
      bg.destroy();
      hit?.destroy();
    },
  };
}

export function makeStandardButton(opts: StandardButtonOpts): StandardButton {
  const {
    scene, x, y, label,
    fontSize = DEFAULT_FONT_SIZE,
    fillColor = FILL_ON,
    paddingX = DEFAULT_PAD_X,
    height = DEFAULT_HEIGHT,
    onClick,
    baseDepth = 10,
  } = opts;

  const text = makeTwoPassText(scene, x, y, label, {
    fontSize,
    depth: baseDepth + 1,
  });
  const textW = text.fill.width - 32; // strip the 16px-each-side text padding
  const width = textW + paddingX;
  const r = height / 2;

  const bg = scene.add.graphics().setDepth(baseDepth);
  bg.fillStyle(fillColor, 1);
  bg.fillRoundedRect(x - width / 2, y - height / 2, width, height, r);
  bg.lineStyle(STROKE_WIDTH, STROKE, 1);
  bg.strokeRoundedRect(x - width / 2, y - height / 2, width, height, r);

  const hit = scene.add
    .rectangle(x, y, width, height, 0x000000, 0)
    .setOrigin(0.5)
    .setDepth(baseDepth + 2)
    .setInteractive({ useHandCursor: true });
  hit.on('pointerdown', onClick);

  return {
    bg,
    hit,
    text,
    width,
    height,
    objects: [bg, text.outline, text.fill, hit],
    destroy(): void {
      text.destroy();
      bg.destroy();
      hit.destroy();
    },
  };
}
